import { Dialog } from '@base-ui/react/dialog';
import { useNavigate } from '@tanstack/react-router';
import { Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { BetaCodeLink } from '../account/BetaCode.tsx';
import { DEMO_BILLING_NOTE, useIsDemo } from '../account/beta.ts';
import { useAccount } from '../account/store.ts';
import { buyCreditPack, checkoutPlan } from '../plans/billing.ts';
import { cheapestPlanWith, formatAmount, PLAN_NAMES, trialEligible } from '../plans/features.ts';
import { PlanSummary } from '../plans/PlanCard.tsx';
import { loadPlans, usePlans } from '../plans/store.ts';
import { TrialOffer } from '../plans/TrialOffer.tsx';
import { setUi, useUi } from '../state/uiStore.ts';
import { Button } from './Button.tsx';
import { Segmented } from './Segmented.tsx';

export const FEATURE_LABELS: Record<string, string> = {
  basic_quote: 'Quote PDF',
  branded_quote: 'Branded quotes',
  quote_templates: 'Quote templates',
  hd_export: 'Full-resolution exports without watermark',
  hdr_export: 'HDR export',
  sync: 'Sync across devices',
  company_branding: 'Company branding',
  shared_catalog: 'A shared price list',
  ai_render: 'Photoreal AI renders',
};

/** Out of credits, or a feature the plan lacks: pre-selected plan, trial, credit packs. */
export function Paywall() {
  const pw = useUi((s) => s.paywall);
  const catalog = usePlans((s) => s.catalog);
  const signedIn = useAccount((a) => !!a.session);
  const activeScope = useAccount((a) => a.activeScope);
  const me = useAccount((a) => a.me);
  const personal = me?.personalScope ?? null;
  const navigate = useNavigate();
  const [interval, setBilling] = useState<'month' | 'year'>('year');
  const [busy, setBusy] = useState<string | null>(null);
  const demo = useIsDemo();

  useEffect(() => {
    if (pw) void loadPlans();
  }, [pw]);

  const planId =
    pw?.plan ??
    (pw?.reason === 'credits'
      ? cheapestPlanWith('ai_render', catalog)
      : (catalog?.plans.find((p) => p.featured)?.id ?? 'pro'));
  const plan = catalog?.plans.find((p) => p.id === planId) ?? null;
  const close = () => setUi({ paywall: null });
  const title =
    pw?.reason === 'credits'
      ? 'You’re out of AI render credits'
      : `${FEATURE_LABELS[pw?.feature ?? ''] ?? 'This feature'} is included in ${plan?.name ?? PLAN_NAMES[planId] ?? planId}`;
  const orgWallet = activeScope && activeScope !== personal ? activeScope : undefined;

  return (
    <Dialog.Root open={!!pw} onOpenChange={(o) => !o && close()}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-[70] bg-scrim" />
        <Dialog.Popup className="fixed top-1/2 left-1/2 z-[71] flex max-h-[92dvh] w-[calc(100vw-32px)] max-w-[440px] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-y-auto rounded-xl border border-border bg-surface p-6 shadow-[var(--shadow-panel)] outline-none">
          <Sparkles className="text-accent" size={28} aria-hidden />
          <Dialog.Title className="t-title">{title}</Dialog.Title>
          {plan ? (
            <>
              <Segmented
                label="Billing interval"
                size="sm"
                value={interval}
                onChange={setBilling}
                options={[
                  { value: 'month', label: 'Monthly' },
                  { value: 'year', label: 'Yearly' },
                ]}
              />
              <div className="rounded-md border border-accent bg-surface2 p-4">
                <PlanSummary plan={plan} interval={interval} catalog={catalog} trialEligible={trialEligible(me)} />
              </div>
              <Button
                variant="primary"
                full
                loading={busy === 'plan'}
                onClick={async () => {
                  if (plan.seats.perSeat) {
                    close();
                    // From inside a company workspace, upgrade that company; otherwise start one.
                    const org = me?.orgs.find(
                      (o) => o.id === (pw?.orgId ?? activeScope) && (o.role === 'owner' || o.role === 'admin'),
                    );
                    if (org)
                      await navigate({ to: '/team/$orgId', params: { orgId: org.id }, search: { tab: 'billing' } });
                    else await navigate({ to: '/team/new' });
                    return;
                  }
                  setBusy('plan');
                  await checkoutPlan(plan.id, interval);
                  setBusy(null);
                }}
                disabled={demo && !plan.seats.perSeat}
              >
                {plan.seats.perSeat ? `Set up ${plan.name}` : `Get ${plan.name}`}
              </Button>
            </>
          ) : (
            <Dialog.Description className="t-body text-dim">{catalog ? '' : 'Loading plans…'}</Dialog.Description>
          )}
          {signedIn ? <TrialOffer onDone={close} /> : null}
          {demo ? (
            <p className="t-caption text-dim" data-testid="demo-note">
              {DEMO_BILLING_NOTE}
            </p>
          ) : null}
          {signedIn && !demo ? <BetaCodeLink /> : null}
          {pw?.reason === 'credits' && catalog?.creditPacks.length ? (
            <div className="flex flex-col gap-2">
              <span className="t-overline text-muted">Or buy credits (never expire)</span>
              <div className="flex flex-wrap gap-2">
                {catalog.creditPacks.map((p) => (
                  <Button
                    key={p.id}
                    size="sm"
                    loading={busy === p.id}
                    disabled={demo}
                    onClick={async () => {
                      setBusy(p.id);
                      await buyCreditPack(p.id, orgWallet);
                      setBusy(null);
                    }}
                  >
                    {p.credits} credits · {formatAmount(p.amount, catalog.currency)}
                  </Button>
                ))}
              </div>
            </div>
          ) : null}
          <Button
            variant="ghost"
            full
            onClick={() => {
              close();
              void navigate({ to: '/plans' });
            }}
          >
            Compare all plans
          </Button>
          <Button variant="ghost" full onClick={close}>
            Not now
          </Button>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
